'use client';

import { useMemo, useRef } from 'react';
import { UISerializableOrder } from '@velocity-exchange/common';
import { useChainClockTs } from 'src/hooks/useChainClockTs';
import { getExpiredOrderIds } from 'src/utils/orderExpiry';
import { useCurrentAccountOrders } from './useOrders';

/**
 * `orderId`s of the current account's open orders that have passed their on-chain
 * `maxTs` (plus the hide buffer). Re-evaluates on the chain clock tick. Used both to
 * hide orders from the UI and (in the placement flow) to pair their cancellation.
 */
export function useExpiredOrderIds(): Set<number> {
	const openOrders = useCurrentAccountOrders();
	const chainTs = useChainClockTs();
	// `chainTs` ticks every second, so this memo recomputes on every tick. Return the
	// previous Set when membership is unchanged so downstream consumers (visible-orders
	// memo, chart order-line effect) don't re-run just because a new Set was allocated.
	const prevIdsRef = useRef<Set<number>>(new Set());

	return useMemo(() => {
		const next = getExpiredOrderIds(openOrders, chainTs);
		const prev = prevIdsRef.current;
		const isSame =
			next.size === prev.size && [...next].every((id) => prev.has(id));
		if (isSame) return prev;
		prevIdsRef.current = next;
		return next;
	}, [openOrders, chainTs]);
}

/**
 * The current account's open orders with expired orders removed. This is what the
 * Orders table and the "Orders (N)" count render, so a dead-but-uncleared order stops
 * showing ~`ORDER_EXPIRY_HIDE_BUFFER_SECONDS` after its `maxTs`.
 *
 * NOTE: expired orders still occupy their on-chain order slot until a keeper/fill/cancel
 * clears them. Anything that reasons about on-chain slot occupancy (e.g. the 32-order
 * limit) must keep using `useCurrentAccountOrders` / `getAllNonZeroOrders`, NOT this.
 */
export function useVisibleAccountOrders(): UISerializableOrder[] {
	const openOrders = useCurrentAccountOrders();
	const expiredIds = useExpiredOrderIds();

	return useMemo(
		() =>
			expiredIds.size === 0
				? openOrders
				: openOrders.filter((order) => !expiredIds.has(order.orderId)),
		[openOrders, expiredIds]
	);
}
